import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock3, Headphones, Home, Pause, Play, SkipBack, SkipForward } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { createSceneSelectionRequestId, getQuickPracticeSceneTrigger } from '../services/SceneSelectionRequest';
import PracticeSettingsService from '../services/PracticeSettingsService';
import {
  buildPracticePlayerPlaylist,
  getPracticePlayerStepCount,
  getPracticePlayerRepeatTarget,
  normalizePracticePlayerMode,
} from '../services/PracticePlayerService';
import { buildPracticePlayerProgress } from '../services/PracticePlayerProgressService';
import { preparePracticePlayerAudio } from '../services/PracticePlayerAudioService';
import {
  createPracticeSessionRecordId,
  getPendingPracticeCheckpoint,
} from '../services/PracticeSessionPersistence';
import { playPracticeSound, unlockPracticeAudio } from '../services/soundEffects';
import { getListeningAudio, unlockListeningAudio } from '../services/ListeningAudioTransport';
import { playSentenceAudio, prepareSentenceAudio } from '../services/SentenceAudioPlayback';
import { getReadingArticleParts } from './ReadingArticlePractice';
import ReadingContentItem from './ReadingContentItem';
import useReadingAudio from '../hooks/useReadingAudio';
import useReadingAudioFollow from '../hooks/useReadingAudioFollow';
import ReadingAudioControls from './ReadingAudioControls';
import { getCalendarImage } from '../services/TrainerAssets';
import { t } from '../i18n';
import {
  getNoteThemeStyle,
  normalizeNoteTheme,
} from '../utils/noteTheme';
import usePageThemeColor from '../hooks/usePageThemeColor';
import usePlayerCoverLayout from '../hooks/usePlayerCoverLayout';
import CelebrationPopup from './CelebrationPopup';
import CoachingTimeOverlay from './CoachingTimeOverlay';
import MarkdownText from './MarkdownText';
import SessionSummary from './SessionSummary';
import Spinner from './Spinner';
import { useConfirmDialog } from './ConfirmDialog';
import { LearningExitButton } from './LearningSessionHeader';
import './PracticePlayerPage.css';

const SESSION_LIMIT_SECONDS = 30 * 60;
const RING_DURATION_MS = 700;
const METRONOME_TICK_MS = 1000;
const BLANK_TICK_COUNT = 2;
const COVER_SCORE_BURST_MS = 1000;
const PLAYBACK_REPEAT_REWARD = 2;
const LISTENING_COACHING_TRIGGER = 'listening.before.coaching';

function playerT(key, replacements = {}) {
  return t(`practicePlayer.page.${key}`, undefined, replacements);
}

function getCardId(card) {
  return card?.id ?? card?._id;
}

function formatClock(seconds) {
  const value = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
}

function formatAudioTime(seconds) {
  const value = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
}

function buildCardSteps(card, mode, options) {
  if (PracticeSettingsService.getCardType(card) === PracticeSettingsService.CARD_TYPES.READING) {
    return ['ring', 'reading'];
  }
  const steps = ['ring', 'question', 'blank'];
  for (let index = 0; index < getPracticePlayerRepeatTarget(mode, card, options); index += 1) {
    steps.push('answer', 'blank');
  }
  return steps;
}

function getPreparedSources(prepared) {
  if (!prepared) return [];
  return prepared.kind === 'reading'
    ? [prepared.content].filter(Boolean)
    : [prepared.question, prepared.answer].filter(Boolean);
}

function getPreparedSource(prepared, step) {
  if (!prepared) return null;
  if (step === 'reading') return prepared.content || null;
  if (step === 'question') return prepared.question || null;
  return prepared.answer || null;
}

function collectNoteMetadata(notes = []) {
  return (Array.isArray(notes) ? notes : []).reduce((result, note) => {
    const noteId = note?.noteId ?? note?.id ?? note?._id;
    if (!noteId) return result;
    result[noteId] = {
      name: note.noteTitle || note.title || note.name || '',
      coverUrl: note.coverUrl || '',
      themeColorTop: note.themeColorTop || null,
      themeColorBottom: note.themeColorBottom || null,
      themeColorLight: note.themeColorLight || null,
      themeColorDark: note.themeColorDark || null,
    };
    return result;
  }, {});
}

function isFatalPreparationError(error) {
  const errorCode = error?.code || error?.data?.error?.code || error?.data?.code;
  return errorCode === 'INVALID_TTS_SOURCE'
    || errorCode === 'TTS_PREPARATION_TIMEOUT'
    || error?.isNetworkError === true
    || error?.status === 401
    || error?.status === 403
    || Number(error?.status) >= 500;
}

function PracticePlayerPage({ flashCards = [], onFlashCardsUpdate, autoStart = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { confirm, alert: showAlert, confirmDialog } = useConfirmDialog();
  const context = location.state?.playerContext || {};
  const mode = normalizePracticePlayerMode(context.mode);
  const isQuickPractice = context.practiceMode === 'quick'
    || context.settings?.practiceMode === 'quick';
  const initialPlaylistRef = useRef(null);
  if (!initialPlaylistRef.current) {
    const sourceCards = Array.isArray(context.flashcards) && context.flashcards.length > 0
      ? context.flashcards
      : flashCards;
    initialPlaylistRef.current = buildPracticePlayerPlaylist(sourceCards, {
      mode,
      settings: context.settings || PracticeSettingsService.loadSettings(),
      includeAllCards: context.includeAllCards === true,
    });
  }

  const [playlist, setPlaylist] = useState(initialPlaylistRef.current);
  const [cardIndex, setCardIndex] = useState(0);
  const readingAudio = useReadingAudio(playlist[cardIndex]?.cardType === 'reading' ? (playlist[cardIndex]?.id || playlist[cardIndex]?._id) : null);
  const getReadingEngine = readingAudio.getEngine;
  const readingEngineRef = useRef(null);
  readingEngineRef.current = readingAudio.engine;
  const readingFollow = useReadingAudioFollow(readingAudio.state);
  const [phase, setPhase] = useState('initializing');
  const [coachingScene, setCoachingScene] = useState(null);
  const [stepProgress, setStepProgress] = useState(0);
  const [stage, setStage] = useState('');
  const [notice, setNotice] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const [noteMetadata, setNoteMetadata] = useState({});
  const [, setAudioCurrentTime] = useState(0);
  const [, setAudioDuration] = useState(0);
  const [sessionResults, setSessionResults] = useState(null);
  const [completionPopup, setCompletionPopup] = useState(null);
  const [fatalError, setFatalError] = useState('');
  const [preparationError, setPreparationError] = useState('');
  const [preparationAttempt, setPreparationAttempt] = useState(0);
  const [coverScoreBurst, setCoverScoreBurst] = useState(null);

  const mountedRef = useRef(true);
  const playlistRef = useRef(playlist);
  const globalCardsRef = useRef(flashCards);
  const cardIndexRef = useRef(0);
  const practiceStartIdRef = useRef(null);
  const practiceSessionRecordIdRef = useRef(null);
  const lastSavedCheckpointRef = useRef(0);
  const checkpointSavePromiseRef = useRef(null);
  const preparedCacheRef = useRef(new Map());
  const preparationControllerRef = useRef(new AbortController());
  const fatalPreparationErrorRef = useRef(null);
  const currentPreparedRef = useRef(null);
  const currentCompletedRef = useRef(0);
  const currentSavedRef = useRef(0);
  const pendingAnswerPlaybackRef = useRef(false);
  const stepCursorRef = useRef(0);
  const runTokenRef = useRef(0);
  const loopActiveRef = useRef(false);
  const pauseRequestedRef = useRef(false);
  const activeDelayCancelRef = useRef(null);
  const activeAudioCancelRef = useRef(null);
  const activeAudioRef = useRef(null);
  const playbackAudioRef = useRef(null);
  const playbackAudioSourceRef = useRef('');
  const elapsedTimeRef = useRef(0);
  const timeLimitReachedRef = useRef(false);
  const sessionStartTimeRef = useRef(null);
  const endingRef = useRef(false);
  const totalRepeatsRef = useRef(0);
  const totalScoreRef = useRef(0);
  const studiedCardIdsRef = useRef(new Set());
  const evaluationsRef = useRef([]);
  const autoStartPendingRef = useRef(autoStart);
  const coachingScenePromiseRef = useRef(null);
  const coverScoreBurstTimerRef = useRef(null);

  useEffect(() => { playlistRef.current = playlist; }, [playlist]);
  useEffect(() => { globalCardsRef.current = flashCards; }, [flashCards]);
  useEffect(() => { cardIndexRef.current = cardIndex; }, [cardIndex]);
  useEffect(() => { elapsedTimeRef.current = elapsedTime; }, [elapsedTime]);

  const clearPlaybackAudioSource = useCallback(() => {
    const audio = playbackAudioRef.current;
    if (!audio) return;
    audio.pause();
    try {
      if (typeof audio.removeAttribute === 'function') audio.removeAttribute('src');
      else audio.src = '';
      if (typeof audio.load === 'function') audio.load();
    } catch (_) {
      // The shared element is still reusable even if Safari rejects a reset.
    }
    playbackAudioSourceRef.current = '';
  }, []);

  const ensurePlaybackAudio = useCallback((prepared, { reload = false } = {}) => {
    const playbackUrl = prepared?.playbackUrl;
    if (!playbackUrl) return null;

    if (!playbackAudioRef.current) {
      playbackAudioRef.current = getListeningAudio();
      playbackAudioRef.current.preload = 'auto';
    }

    const audio = playbackAudioRef.current;
    if (reload || playbackAudioSourceRef.current !== playbackUrl || audio.src !== playbackUrl) {
      audio.pause();
      audio.src = playbackUrl;
      audio.preload = 'auto';
      playbackAudioSourceRef.current = playbackUrl;
      if (typeof audio.load === 'function') audio.load();
    }
    return audio;
  }, []);

  const releasePrepared = useCallback((index) => {
    const entry = preparedCacheRef.current.get(index);
    if (!entry) return;
    entry.promise?.then((prepared) => {
      if (currentPreparedRef.current === prepared) {
        clearPlaybackAudioSource();
        currentPreparedRef.current = null;
      }
      getPreparedSources(prepared).forEach((source) => {
        if (source.objectUrl) URL.revokeObjectURL(source.objectUrl);
      });
    }).catch(() => {});
    preparedCacheRef.current.delete(index);
  }, [clearPlaybackAudioSource]);

  const stopActiveStep = useCallback(() => {
    readingEngineRef.current?.stop();
    runTokenRef.current += 1;
    pauseRequestedRef.current = false;
    activeDelayCancelRef.current?.();
    activeDelayCancelRef.current = null;
    if (activeAudioRef.current) activeAudioRef.current.pause();
    activeAudioCancelRef.current?.();
    activeAudioCancelRef.current = null;
    activeAudioRef.current = null;
    loopActiveRef.current = false;
  }, []);

  const clearCoverScoreBurst = useCallback(() => {
    if (coverScoreBurstTimerRef.current) {
      clearTimeout(coverScoreBurstTimerRef.current);
      coverScoreBurstTimerRef.current = null;
    }
    setCoverScoreBurst(null);
  }, []);

  useEffect(() => {
    const preparedCache = preparedCacheRef.current;
    mountedRef.current = true;
    if (preparationControllerRef.current.signal.aborted) {
      preparationControllerRef.current = new AbortController();
    }
    return () => {
      mountedRef.current = false;
      stopActiveStep();
      clearCoverScoreBurst();
      preparationControllerRef.current.abort();
      [...preparedCache.keys()].forEach(releasePrepared);
      clearPlaybackAudioSource();
      playbackAudioRef.current = null;
    };
  }, [clearCoverScoreBurst, clearPlaybackAudioSource, releasePrepared, stopActiveStep]);

  const delay = useCallback((milliseconds, token) => new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (activeDelayCancelRef.current === cancel) activeDelayCancelRef.current = null;
      resolve(result);
    };
    const timer = setTimeout(() => finish(runTokenRef.current === token), milliseconds);
    const cancel = () => finish(false);
    activeDelayCancelRef.current = cancel;
  }), []);

  const prepareCardAt = useCallback((index) => {
    if (index < 0 || index >= playlistRef.current.length) return Promise.reject(new Error('Card is outside the playlist.'));
    const cached = preparedCacheRef.current.get(index);
    if (cached) return cached.promise;

    const card = playlistRef.current[index];
    const entry = {};
    entry.promise = preparePracticePlayerAudio(card, {
      signal: preparationControllerRef.current.signal,
    }).then((result) => {
      if (!mountedRef.current) throw Object.assign(new Error('Player closed'), { name: 'AbortError' });
      const attachPlaybackUrl = (source) => {
        const objectUrl = source?.blob ? URL.createObjectURL(source.blob) : null;
        const playbackUrl = source?.audioUrl || objectUrl;
        if (!playbackUrl) throw new Error('Listening audio is unavailable.');
        return { ...source, objectUrl, playbackUrl };
      };
      return result.kind === 'reading'
        ? result
        : {
          ...result,
          question: attachPlaybackUrl(result.question),
          answer: attachPlaybackUrl(result.answer),
        };
    });
    preparedCacheRef.current.set(index, entry);
    return entry.promise;
  }, []);

  const prefetchAhead = useCallback((index) => {
    void (async () => {
      for (const nextIndex of [index + 1, index + 2]) {
        if (nextIndex >= playlistRef.current.length) break;
        try {
          await prepareCardAt(nextIndex);
        } catch (_) {
          // The card will be skipped when the player reaches it.
        }
      }
    })();
  }, [prepareCardAt]);

  const activatePlayableCard = useCallback(async (startIndex, { shouldPlay = hasStarted, exact = false } = {}) => {
    setPhase('preparing');
    setPreparationError('');
    fatalPreparationErrorRef.current = null;
    let lastError = null;
    for (let index = startIndex; index < playlistRef.current.length; index += 1) {
      setPreparationAttempt(index + 1);
      try {
        const prepared = await prepareCardAt(index);
        if (!mountedRef.current) return false;
        // Detach the previous Reading engine before a different card uses the
        // shared element; React's effect cleanup would otherwise run too late.
        readingEngineRef.current = getReadingEngine(prepared.kind === 'reading' ? (playlistRef.current[index].id || playlistRef.current[index]._id) : null);
        cardIndexRef.current = index;
        setCardIndex(index);
        currentPreparedRef.current = prepared;
        ensurePlaybackAudio(getPreparedSource(prepared, prepared.kind === 'reading' ? 'reading' : 'question'));
        currentCompletedRef.current = 0;
        currentSavedRef.current = 0;
        pendingAnswerPlaybackRef.current = false;
        clearCoverScoreBurst();
        stepCursorRef.current = 0;
        setStepProgress(0);
        setStage('');
        setAudioCurrentTime(0);
        setAudioDuration(0);
        setPreparationError('');
        fatalPreparationErrorRef.current = null;
        setPhase(shouldPlay ? 'playing' : 'ready');
        prefetchAhead(index);
        return true;
      } catch (error) {
        if (error.name === 'AbortError') return false;
        lastError = error;
        setPreparationError(error?.message || playerT('skipped'));
        setNotice(playerT('skipped'));
        releasePrepared(index);
        if (isFatalPreparationError(error)) {
          fatalPreparationErrorRef.current = error;
          break;
        }
        if (exact) break;
      }
    }
    setPreparationError(lastError?.message || '');
    return false;
  }, [clearCoverScoreBurst, ensurePlaybackAudio, getReadingEngine, hasStarted, prefetchAhead, prepareCardAt, releasePrepared]);

  useEffect(() => {
    let cancelled = false;
    const initialize = async () => {
      if (playlistRef.current.length === 0) {
        setFatalError(playerT('noPlayable'));
        setPhase('error');
        return;
      }
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const profile = await apiClient.getCurrentUser();
        if (cancelled) return;
        if (profile?.entitlements?.canUseTts !== true) {
          await showAlert({
            title: t('membership.tts.title'),
            message: t('membership.tts.message'),
            confirmLabel: t('common.ok'),
            tone: 'warning',
          });
          navigate('/');
          return;
        }
        const [access, notesResponse] = await Promise.all([
          isQuickPractice && context.access
            ? Promise.resolve(context.access)
            : apiClient.startPractice({ timezone, mode: `player.${mode}` }),
          isQuickPractice
            ? Promise.resolve([])
            : apiClient.getAllNotes().catch(() => []),
        ]);
        if (cancelled) return;
        if (access?.entitlements?.canUseTts === false) {
          await showAlert({
            title: t('membership.tts.title'),
            message: t('membership.tts.message'),
            confirmLabel: t('common.ok'),
            tone: 'warning',
          });
          navigate('/');
          return;
        }
        practiceStartIdRef.current = access?.practiceStartId || null;
        practiceSessionRecordIdRef.current = createPracticeSessionRecordId(
          practiceStartIdRef.current,
          `player-${mode}`
        );
        setNoteMetadata(collectNoteMetadata(notesResponse?.notes || notesResponse));
        // Select once per entry, without a fixed seed or the daily coaching limit.
        if (!coachingScenePromiseRef.current) {
          const trigger = (isQuickPractice && getQuickPracticeSceneTrigger(context.source || context.settings?.quickSource))
            || LISTENING_COACHING_TRIGGER;
          coachingScenePromiseRef.current = Promise.resolve()
            .then(() => apiClient.getRandomScene({ trigger, timezone, requestId: createSceneSelectionRequestId() }))
            .catch((error) => {
              console.warn('Failed to load listening coaching scene:', error);
              return null;
            });
        }
        const coachingResult = await coachingScenePromiseRef.current;
        if (cancelled) return;
        if (coachingResult?.scene?.frames?.some((frame) => typeof frame?.text === 'string' && frame.text.trim())) {
          setCoachingScene(coachingResult.scene);
        }
        const found = await activatePlayableCard(0);
        if (!found && !cancelled) {
          setFatalError(fatalPreparationErrorRef.current
            ? playerT('serviceUnavailable')
            : playerT('noPlayable'));
          setPhase('error');
        }
      } catch (error) {
        if (cancelled) return;
        setFatalError(error.message || t('membership.practiceUnavailable.message'));
        setPhase('error');
      }
    };
    void initialize();
    return () => { cancelled = true; };
  // Initialization intentionally uses the navigation snapshot once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasStarted || !['playing', 'pausing'].includes(phase) || sessionResults || endingRef.current) return undefined;
    const timer = setInterval(() => {
      setElapsedTime((current) => {
        const next = current + 1;
        elapsedTimeRef.current = next;
        if (next >= SESSION_LIMIT_SECONDS) timeLimitReachedRef.current = true;
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [hasStarted, phase, sessionResults]);

  const syncUpdatedCard = useCallback((updatedCard) => {
    const cardId = getCardId(updatedCard);
    if (!cardId) return;
    const nextPlaylist = playlistRef.current.map((card) => (
      getCardId(card) === cardId ? { ...card, ...updatedCard } : card
    ));
    playlistRef.current = nextPlaylist;
    setPlaylist(nextPlaylist);

    const nextGlobalCards = globalCardsRef.current.map((card) => (
      getCardId(card) === cardId ? { ...card, ...updatedCard } : card
    ));
    globalCardsRef.current = nextGlobalCards;
    onFlashCardsUpdate?.(nextGlobalCards);
  }, [onFlashCardsUpdate]);

  const persistCurrentProgress = useCallback(async () => {
    const delta = Math.max(0, currentCompletedRef.current - currentSavedRef.current);
    if (delta <= 0) return;
    const index = cardIndexRef.current;
    const card = playlistRef.current[index];
    if (!card) return;
    const progress = buildPracticePlayerProgress(card, delta, mode);
    currentSavedRef.current += delta;
    if (isQuickPractice) {
      totalRepeatsRef.current += progress.repeatDelta;
      totalScoreRef.current += progress.scoreDelta;
      studiedCardIdsRef.current.add(getCardId(card));
      return;
    }
    try {
      const response = await apiClient.savePracticeProgressBatch({
        updates: [progress.update],
        practiceStartId: practiceStartIdRef.current,
      });
      const updatedCard = response?.updatedCards?.[0] || progress.updatedCard;
      syncUpdatedCard(updatedCard);
      totalRepeatsRef.current += progress.repeatDelta;
      totalScoreRef.current += progress.scoreDelta;
      studiedCardIdsRef.current.add(getCardId(card));
      if (progress.evaluation) {
        evaluationsRef.current.push({ cardId: getCardId(card), ...progress.evaluation });
      }
    } catch (error) {
      currentSavedRef.current -= delta;
      throw error;
    }
  }, [isQuickPractice, mode, syncUpdatedCard]);

  const playPreparedAudio = useCallback(async (prepared, token) => {
    const audio = ensurePlaybackAudio(prepared);
    if (!audio || runTokenRef.current !== token) return false;
    const playback = playSentenceAudio(audio, {
      reload: () => ensurePlaybackAudio(prepared, { reload: true }),
      isCurrent: () => runTokenRef.current === token,
    });
    activeAudioCancelRef.current = playback.cancel;
    activeAudioRef.current = audio;
    const played = await playback.finished;
    if (activeAudioCancelRef.current === playback.cancel) {
      activeAudioCancelRef.current = null;
      activeAudioRef.current = null;
    }
    return played && runTokenRef.current === token;
  }, [ensurePlaybackAudio]);

  const awardPlaybackCycle = useCallback((card) => {
    const beforeRepeats = currentCompletedRef.current;
    const beforeScore = buildPracticePlayerProgress(card, beforeRepeats, mode).scoreDelta;
    const afterRepeats = beforeRepeats + PLAYBACK_REPEAT_REWARD;
    const afterScore = buildPracticePlayerProgress(card, afterRepeats, mode).scoreDelta;
    const scoreReward = Math.max(0, afterScore - beforeScore);

    currentCompletedRef.current = afterRepeats;
    if (scoreReward <= 0) return;

    if (coverScoreBurstTimerRef.current) clearTimeout(coverScoreBurstTimerRef.current);
    setCoverScoreBurst({ id: Date.now(), score: scoreReward });
    coverScoreBurstTimerRef.current = setTimeout(() => {
      coverScoreBurstTimerRef.current = null;
      setCoverScoreBurst(null);
    }, COVER_SCORE_BURST_MS);
  }, [mode]);

  const executeStep = useCallback(async (step, token, nextStep) => {
    const card = playlistRef.current[cardIndexRef.current];
    if (!card) return false;
    setStage(step);

    if (step === 'ring') {
      void playPracticeSound('playerRing', { force: true });
      return delay(RING_DURATION_MS, token);
    }
    if (step === 'question') {
      const played = await playPreparedAudio(
        getPreparedSource(currentPreparedRef.current, 'question'),
        token
      );
      if (played) setNotice('');
      return played;
    }
    if (step === 'blank') {
      if (nextStep === 'answer') {
        prepareSentenceAudio(ensurePlaybackAudio(getPreparedSource(currentPreparedRef.current, 'answer')));
      }
      for (let tick = 0; tick < BLANK_TICK_COUNT; tick += 1) {
        void playPracticeSound('playerTick', { force: true });
        if (!await delay(METRONOME_TICK_MS, token)) return false;
      }
      if (pendingAnswerPlaybackRef.current) {
        pendingAnswerPlaybackRef.current = false;
        awardPlaybackCycle(card);
      }
      return true;
    }
    if (step === 'answer' || step === 'reading') {
      if (step === 'reading') {
        const engine = getReadingEngine(card.id || card._id);
        readingEngineRef.current = engine;
        const result = await engine.playPage();
        if (runTokenRef.current !== token || !result?.finished) return false;
        if (result.eligible) awardPlaybackCycle(card);
        setNotice('');
        return true;
      }
      const played = await playPreparedAudio(
        getPreparedSource(currentPreparedRef.current, step),
        token
      );
      if (played && step === 'answer') pendingAnswerPlaybackRef.current = true;
      if (played && step === 'reading') awardPlaybackCycle(card);
      if (played) setNotice('');
      return played;
    }
    return true;
  }, [awardPlaybackCycle, delay, ensurePlaybackAudio, playPreparedAudio, getReadingEngine]);

  const persistSessionSnapshot = useCallback(async (isComplete) => {
    const finalElapsed = elapsedTimeRef.current;
    const persistedDuration = Math.max(1, finalElapsed);
    if (!practiceSessionRecordIdRef.current) {
      practiceSessionRecordIdRef.current = createPracticeSessionRecordId(
        practiceStartIdRef.current,
        `player-${mode}`
      );
    }
    return apiClient.savePracticeSession({
      sessionId: practiceSessionRecordIdRef.current,
      startTime: sessionStartTimeRef.current || new Date(Date.now() - persistedDuration * 1000).toISOString(),
      endTime: new Date().toISOString(),
      duration: persistedDuration,
      cardsStudied: studiedCardIdsRef.current.size,
      repeatCount: totalRepeatsRef.current,
      scoreGained: totalScoreRef.current,
      practiceStartId: practiceStartIdRef.current,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      mode: isQuickPractice ? 'quick' : `player.${mode}`,
      isComplete,
    });
  }, [isQuickPractice, mode]);

  const saveSession = useCallback(async (endReason) => {
    const finalElapsed = elapsedTimeRef.current;
    const practicedCards = playlistRef.current.filter((card) => studiedCardIdsRef.current.has(getCardId(card)));
    let dailyStats = null;
    try {
      const response = await persistSessionSnapshot(true);
      dailyStats = response?.dailyStats || null;
    } catch (error) {
      console.error('Failed to save Practice Player session:', error);
    }

    const results = {
      cardsCompleted: studiedCardIdsRef.current.size,
      cardsStudied: studiedCardIdsRef.current.size,
      evaluations: evaluationsRef.current,
      elapsedTime: finalElapsed,
      repeatCount: totalRepeatsRef.current,
      scoreGained: totalScoreRef.current,
      practicedCards,
      dailyStats,
      endReason,
      practiceMode: isQuickPractice ? 'quick' : `player.${mode}`,
    };
    setSessionResults(results);
    setPhase('complete');
    window.dispatchEvent(new CustomEvent('practiceSessionComplete', {
      detail: { sessionResults: results, timestamp: new Date().toISOString() },
    }));
    try { sessionStorage.setItem('pendingSessionResults', JSON.stringify(results)); } catch (_) {}
  }, [isQuickPractice, mode, persistSessionSnapshot]);

  useEffect(() => {
    const checkpointSeconds = getPendingPracticeCheckpoint(
      elapsedTime,
      lastSavedCheckpointRef.current
    );
    if (!checkpointSeconds || checkpointSavePromiseRef.current || endingRef.current) return;

    const checkpointPromise = (async () => {
      try {
        await persistCurrentProgress();
        await persistSessionSnapshot(false);
        lastSavedCheckpointRef.current = checkpointSeconds;
      } catch (error) {
        console.warn(`Failed to save ${checkpointSeconds / 60}-minute player checkpoint:`, error);
      }
    })();
    checkpointSavePromiseRef.current = checkpointPromise;
    checkpointPromise.finally(() => {
      if (checkpointSavePromiseRef.current === checkpointPromise) {
        checkpointSavePromiseRef.current = null;
      }
    });
  }, [elapsedTime, persistCurrentProgress, persistSessionSnapshot]);

  const finishSession = useCallback(async (endReason, { stop = false } = {}) => {
    if (endingRef.current) return;
    endingRef.current = true;
    if (stop) stopActiveStep();
    setPhase('saving');
    try {
      if (checkpointSavePromiseRef.current) {
        await checkpointSavePromiseRef.current;
      }
      await persistCurrentProgress();
      preparationControllerRef.current.abort();
      [...preparedCacheRef.current.keys()].forEach(releasePrepared);
      await saveSession(endReason);
      if (endReason === 'timeLimit') {
        setCompletionPopup({
          title: playerT('timeTitle'),
          message: playerT('timeMessage'),
        });
      } else if (endReason === 'queueExhausted') {
        setCompletionPopup({
          title: playerT('completeTitle'),
          message: playerT('completeMessage'),
        });
      }
    } catch (error) {
      endingRef.current = false;
      setFatalError(error.message || 'Practice progress could not be saved.');
      setPhase('error');
    }
  }, [persistCurrentProgress, releasePrepared, saveSession, stopActiveStep]);

  const runCurrentCard = useCallback(async () => {
    if (loopActiveRef.current || endingRef.current) return;
    loopActiveRef.current = true;
    const token = ++runTokenRef.current;
    setPhase('playing');
    const card = playlistRef.current[cardIndexRef.current];
    const steps = buildCardSteps(card, mode, { isQuickPractice });

    while (stepCursorRef.current < steps.length && runTokenRef.current === token) {
      const succeeded = await executeStep(steps[stepCursorRef.current], token, steps[stepCursorRef.current + 1]);
      if (runTokenRef.current !== token) {
        loopActiveRef.current = false;
        return;
      }
      if (!succeeded) {
        pauseRequestedRef.current = false;
        loopActiveRef.current = false;
        setNotice(playerT('playbackInterrupted'));
        setPhase('paused');
        return;
      }
      stepCursorRef.current += 1;
      setStepProgress(stepCursorRef.current);
      if (pauseRequestedRef.current) {
        pauseRequestedRef.current = false;
        loopActiveRef.current = false;
        setPhase('paused');
        return;
      }
    }

    loopActiveRef.current = false;
    try {
      await persistCurrentProgress();
    } catch (error) {
      setFatalError(error.message || 'Practice progress could not be saved.');
      setPhase('error');
      return;
    }
    releasePrepared(cardIndexRef.current);

    if (timeLimitReachedRef.current) {
      void finishSession('timeLimit');
      return;
    }

    const found = await activatePlayableCard(cardIndexRef.current + 1, { shouldPlay: true });
    if (!found) {
      if (fatalPreparationErrorRef.current) {
        setFatalError(playerT('serviceUnavailable'));
        setPhase('error');
        return;
      }
      void finishSession('queueExhausted');
      return;
    }
    void runCurrentCard();
  }, [activatePlayableCard, executeStep, finishSession, isQuickPractice, mode, persistCurrentProgress, releasePrepared]);

  const handlePlayPause = useCallback(async () => {
    const activeCard = playlistRef.current[cardIndexRef.current];
    const isReadingCard = PracticeSettingsService.getCardType(activeCard) === PracticeSettingsService.CARD_TYPES.READING;
    if (isReadingCard && readingEngineRef.current && loopActiveRef.current && stage === 'reading') {
      const engine = readingEngineRef.current;
      if (['playing', 'loading', 'gap'].includes(engine.state.status)) { engine.pause(); setPhase('paused'); }
      else { void engine.resume(); setPhase('playing'); }
      return;
    }

    if (phase === 'playing') {
      if (isReadingCard && activeAudioRef.current) {
        activeAudioRef.current.pause();
        setPhase('paused');
        return;
      }
      pauseRequestedRef.current = true;
      setPhase('pausing');
      return;
    }
    if (phase !== 'ready' && phase !== 'paused') return;
    void unlockListeningAudio();
    await unlockPracticeAudio({ forceEnabled: true });
    if (phase === 'paused' && isReadingCard && activeAudioRef.current) {
      try {
        await activeAudioRef.current.play();
        setNotice('');
        setPhase('playing');
      } catch (_) {
        setNotice(playerT('playbackInterrupted'));
      }
      return;
    }
    if (!hasStarted) {
      sessionStartTimeRef.current = new Date().toISOString();
      setHasStarted(true);
    }
    pauseRequestedRef.current = false;
    void runCurrentCard();
  }, [hasStarted, phase, runCurrentCard, stage]);

  useEffect(() => {
    if (coachingScene || !autoStartPendingRef.current || phase !== 'ready') return;
    autoStartPendingRef.current = false;
    void handlePlayPause();
  }, [coachingScene, handlePlayPause, phase]);

  const continueAfterCoaching = () => {
    // Keep the iOS audio unlock inside the user's scene-continue gesture, even
    // when the first card still needs time to finish preparing its audio.
    void unlockListeningAudio();
    void unlockPracticeAudio({ forceEnabled: true });
    setCoachingScene(null);
  };

  const handleEndPractice = async () => {
    const accepted = await confirm({
      title: playerT('endTitle'),
      message: playerT('endMessage'),
      confirmLabel: playerT('end'),
      cancelLabel: t('common.cancel'),
      tone: 'warning',
    });
    if (accepted) void finishSession(null, { stop: true });
  };

  const handleCardNavigation = async (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= playlistRef.current.length || endingRef.current) return;
    stopActiveStep();
    setPhase('preparing');
    setNotice('');
    try {
      await persistCurrentProgress();
      releasePrepared(cardIndexRef.current);
      const found = await activatePlayableCard(targetIndex, { shouldPlay: false, exact: true });
      if (!found && mountedRef.current) {
        setFatalError(fatalPreparationErrorRef.current
          ? playerT('serviceUnavailable')
          : playerT('noPlayable'));
        setPhase('error');
      }
    } catch (error) {
      setFatalError(error.message || 'Practice progress could not be saved.');
      setPhase('error');
    }
  };

  const handleRetryPreparation = async () => {
    setFatalError('');
    setPreparationError('');
    setNotice('');
    const found = await activatePlayableCard(0, { shouldPlay: false });
    if (!found && mountedRef.current) {
      setFatalError(fatalPreparationErrorRef.current
        ? playerT('serviceUnavailable')
        : playerT('noPlayable'));
      setPhase('error');
    }
  };

  const currentCard = playlist[cardIndex] || null;
  const isReading = PracticeSettingsService.getCardType(currentCard) === PracticeSettingsService.CARD_TYPES.READING;
  const readingParts = useMemo(() => currentCard && isReading ? getReadingArticleParts(currentCard) : null, [currentCard, isReading]);
  const note = (currentCard?.noteId ? noteMetadata[currentCard.noteId] : null) || context.note || null;
  const noteTheme = useMemo(
    () => normalizeNoteTheme(note || {}),
    [note],
  );
  const noteThemeStyle = getNoteThemeStyle(note);
  usePageThemeColor(noteTheme);
  const playerCover = usePlayerCoverLayout(note?.coverUrl);
  const noteName = note?.name || currentCard?.noteName || currentCard?.noteTitle || '';
  const totalSteps = isReading
    ? readingAudio.state.units.length || readingParts?.displayItems.filter(item => item.type !== 'scene').length || 0
    : currentCard ? getPracticePlayerStepCount(mode, currentCard, { isQuickPractice }) : 0;
  const displayStepProgress = isReading ? Math.max(0, Math.min(totalSteps, readingAudio.state.index + 1)) : stepProgress;
  const progressPercent = totalSteps > 0 ? Math.min(100, (displayStepProgress / totalSteps) * 100) : 0;
  const stageLabel = phase === 'pausing'
    ? playerT('pausing')
    : stage ? playerT(stage) : playerT(phase);

  if (coachingScene && phase !== 'error') {
    return <CoachingTimeOverlay message={coachingScene} onContinue={continueAfterCoaching} />;
  }

  if (phase === 'initializing' || phase === 'preparing') {
    return (
      <div className="practice-player-loading">
        <Spinner size="large" message={playerT('preparing')} />
        {phase === 'preparing' && preparationAttempt > 0 && (
          <span className="practice-player-preparation-count">{preparationAttempt} / {playlist.length}</span>
        )}
        {preparationError && <p className="practice-player-error-detail">{preparationError}</p>}
        {confirmDialog}
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="practice-player-error">
        <Headphones size={40} aria-hidden="true" />
        <h2>{playerT('title')}</h2>
        <p>{fatalError}</p>
        {preparationError && (
          <p className="practice-player-error-detail">
            {preparationError === 'Listening audio preparation timed out. Please try again.'
              ? playerT('preparationTimedOut')
              : preparationError}
          </p>
        )}
        <div className="practice-player-error-actions">
          {playlist.length > 0 && (
            <button type="button" onClick={handleRetryPreparation}>
              <Play size={18} aria-hidden="true" /> {playerT('retry')}
            </button>
          )}
          <button type="button" onClick={() => navigate('/')}><Home size={18} aria-hidden="true" /> {playerT('home')}</button>
        </div>
        {confirmDialog}
      </div>
    );
  }

  if (phase === 'complete' && sessionResults) {
    return (
      <div className="practice-view practice-result-view practice-player-results">
        <SessionSummary
          sessionResults={sessionResults}
          entranceEnabled={!completionPopup}
          actions={(
            <button type="button" onClick={() => navigate(
              isQuickPractice && context.returnTo ? context.returnTo : '/',
              isQuickPractice && context.returnState ? { state: context.returnState } : undefined
            )} className="btn-back btn-result-home">
              <Home size={18} aria-hidden="true" />
              <span>{isQuickPractice ? t('quickPractice.back') : playerT('home')}</span>
            </button>
          )}
        />
        <CelebrationPopup
          isVisible={Boolean(completionPopup)}
          onClose={() => setCompletionPopup(null)}
          title={completionPopup?.title || ''}
          message={completionPopup?.message || ''}
          iconSrc={getCalendarImage('cheerful')}
          iconAlt={playerT('trainerAlt')}
        />
      </div>
    );
  }

  // Reading scrolls its artwork together with the title and text. Its cover has
  // a regular flow size, rather than the sentence player's measured backdrop.
  const coverLayout = isReading ? null : playerCover.layout;
  const album = (
    <section className="practice-player-album" ref={isReading ? undefined : playerCover.albumRef} onLoadCapture={isReading ? undefined : playerCover.onLoad}>
      <div className={`practice-player-cover${coverLayout ? ' is-contained' : ''}`} style={coverLayout || undefined} aria-label={noteName || playerT('currentCover')}>
        {note?.coverUrl ? <img src={note.coverUrl} alt="" /> : <Headphones size={54} strokeWidth={1.5} aria-hidden="true" />}
        {!isReading && noteName && <div className="practice-player-note-name">{noteName}</div>}
        {coverScoreBurst && (
          <div key={coverScoreBurst.id} className="practice-player-cover-score" aria-live="polite">
            +{coverScoreBurst.score} pt
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div
      className={`practice-player-page ${isReading ? 'has-reading-content' : 'has-card-content'}${noteThemeStyle ? ' has-note-theme' : ''}`}
      style={noteThemeStyle}
    >
      {phase === 'saving' && (
        <div className="practice-player-saving"><Spinner size="medium" message={playerT('saving')} /></div>
      )}
      <header className="practice-player-header">
        <LearningExitButton className="practice-player-end" onClick={handleEndPractice} disabled={phase === 'saving'}>
          {playerT('end')}
        </LearningExitButton>
        <div className="practice-player-header-stats">
          <div className="practice-player-timer">
            <Clock3 size={18} aria-hidden="true" />
            <span>{formatClock(elapsedTime)} / 30:00</span>
          </div>
          {!isReading && (
            <span className="practice-player-track-count">{cardIndex + 1} / {playlist.length}</span>
          )}
        </div>
      </header>

      {!isReading && album}

      <main ref={isReading ? readingFollow.ref : undefined} className={`practice-player-content ${isReading ? 'is-reading' : 'is-card'}`.trim()}>
        {isReading ? (
          <>
            {album}
            <div className="practice-player-reading-heading">
              <div className="reading-practice-kicker">Reading</div>
              <h2 className="reading-practice-title">{readingParts?.title || currentCard?.front}</h2>
            </div>
            <div className="practice-player-reading-text">
              {(readingParts?.displayItems || []).map((item, index) => <ReadingContentItem key={index}
                item={item} characterNames={readingParts.characterNames} className={readingFollow.classFor(item)} />)}
            </div>
          </>
        ) : (
          <>
            <section className={`practice-player-copy is-question ${stage === 'question' ? 'is-active' : ''}`.trim()}>
              <MarkdownText text={currentCard?.front || ''} />
            </section>
            <section className={`practice-player-copy is-answer ${stage === 'answer' ? 'is-active' : ''}`.trim()}>
              <MarkdownText text={currentCard?.back || ''} />
            </section>
          </>
        )}
      </main>

      <div className="practice-player-status" aria-live="polite">
          <span>{stageLabel}</span>
          <span className="practice-player-step-count">{displayStepProgress} / {totalSteps}</span>
      </div>
      <div className="practice-player-step-bar" role="progressbar" aria-valuemin={0} aria-valuemax={totalSteps} aria-valuenow={displayStepProgress} aria-label={playerT('stepProgress')}>
          <div style={{ width: `${progressPercent}%` }} />
      </div>

      {isReading && (
        <ReadingAudioControls audio={readingAudio} showAutoplayToggle={false} />
      )}

      {notice && <div className="practice-player-notice" role="status">{notice}</div>}

      <div className="practice-player-controls">
        <button
          type="button"
          className="practice-player-skip-control"
          onClick={() => void handleCardNavigation(cardIndex - 1)}
          disabled={cardIndex <= 0 || ['preparing', 'saving', 'pausing'].includes(phase)}
          aria-label={playerT('previousCard')}
        >
          <SkipBack size={28} fill="currentColor" aria-hidden="true" />
        </button>
        <button
          type="button"
          className={`practice-player-main-control ${phase === 'playing' || phase === 'pausing' ? 'is-playing' : ''}`.trim()}
          onClick={handlePlayPause}
          disabled={phase === 'preparing' || phase === 'saving' || phase === 'pausing'}
          aria-label={phase === 'playing' || phase === 'pausing'
            ? playerT('pauseControl')
            : playerT('playControl')}
        >
          {phase === 'playing' || phase === 'pausing' ? <Pause size={30} fill="currentColor" aria-hidden="true" /> : <Play size={30} fill="currentColor" aria-hidden="true" />}
        </button>
        <button
          type="button"
          className="practice-player-skip-control"
          onClick={() => void handleCardNavigation(cardIndex + 1)}
          disabled={cardIndex >= playlist.length - 1 || ['preparing', 'saving', 'pausing'].includes(phase)}
          aria-label={playerT('nextCard')}
        >
          <SkipForward size={28} fill="currentColor" aria-hidden="true" />
        </button>
      </div>
      {confirmDialog}
    </div>
  );
}

export { buildCardSteps, collectNoteMetadata, formatAudioTime, formatClock, isFatalPreparationError };
export default PracticePlayerPage;
